// src/layout/KuizyLayout.jsx
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import TrialBanner from '@/components/TrialBanner'
import { useSubscription } from '@/context/SubscriptionContext'
import { createContext, useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'

export const LayoutContext = createContext()

function cx(...a) {
  return a.filter(Boolean).join(' ')
}

/** Overlay หลัก ที่จะถูก render จาก Layout */
function GlobalLoadingOverlay({ state }) {
  const {
    open,
    variant = 'spinner', // 'spinner' | 'text' | 'imageSplit'
    text = 'กำลังโหลดข้อมูล...',
    subtext,
    fullscreen = true,
    dimBackground = true,
    size = 56, // px สำหรับ spinner
    imageUrl,
    imageAlt = 'Loading',
    split = '40/60', // '40/60' หรือ '60/40'
  } = state

  if (!open) return null

  const spinnerStyle = {
    width: size,
    height: size,
    borderWidth: Math.max(4, Math.round(size / 8)),
  }

  const spinner = (
    <div
      aria-label="loading"
      className="rounded-full border-t-transparent animate-spin"
      style={spinnerStyle}
    />
  )

  /** overlay base */
  const Overlay = (
    <div
      className={cx(
        'pointer-events-auto fixed inset-0 z-[9990] flex items-center justify-center p-4',
        dimBackground && 'bg-black/40 backdrop-blur-sm'
      )}
      role="dialog"
      aria-modal="true"
    >
      {/* variant: spinner */}
      {variant === 'spinner' && (
        <div className="rounded-2xl bg-white shadow-xl px-6 py-8 flex items-center gap-4">
          {spinner}
          <span className="text-base font-medium">กำลังดำเนินการ...</span>
        </div>
      )}

      {/* variant: text */}
      {variant === 'text' && (
        <div className="rounded-2xl bg-white shadow-xl px-6 py-6 max-w-lg w-full">
          <div className="flex items-start gap-4">
            {spinner}
            <div className="min-w-0">
              <div className="text-lg font-semibold break-words">{text}</div>
              {subtext && <div className="text-sm text-gray-600 mt-1 break-words">{subtext}</div>}
            </div>
          </div>
        </div>
      )}

      {/* variant: imageSplit */}
      {variant === 'imageSplit' && (
        <div className="rounded-2xl bg-white shadow-2xl w-full max-w-5xl overflow-hidden">
          <div className="grid grid-cols-5 min-h-[320px]">
            {/* เลือกสัดส่วน 40/60 หรือ 60/40 */}
            {split === '40/60' ? (
              <>
                <div className="col-span-2 relative bg-gray-100">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      ไม่มีรูปภาพ
                    </div>
                  )}
                </div>
                <div className="col-span-3 p-8 flex flex-col justify-center">
                  <div className="flex items-start gap-4">
                    {spinner}
                    <div>
                      <div className="text-2xl font-semibold">{text}</div>
                      {subtext && <div className="text-gray-600 mt-2">{subtext}</div>}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="col-span-3 p-8 flex flex-col justify-center">
                  <div className="flex items-start gap-4">
                    {spinner}
                    <div>
                      <div className="text-2xl font-semibold">{text}</div>
                      {subtext && <div className="text-gray-600 mt-2">{subtext}</div>}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 relative bg-gray-100">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      ไม่มีรูปภาพ
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )

  // เลือกจะ full screen หรือจะ anchor อยู่ใน main ก็ได้ (แต่ส่วนใหญ่ full screen)
  if (!fullscreen) return Overlay

  // สร้าง portal ไปที่ body เพื่อให้ทับทุกอย่างจริง ๆ
  return createPortal(Overlay, document.body)
}


const BLOCKED_STATUSES = ['expired', 'canceled', 'past_due']

export default function KuizyLayout() {
  const { subscription, loading: subLoading } = useSubscription()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!subLoading && subscription && BLOCKED_STATUSES.includes(subscription.status)) {
      if (location.pathname !== '/subscription') {
        navigate('/subscription', { replace: true })
      }
    }
  }, [subscription, subLoading, location.pathname, navigate])

  const rolesRaw = localStorage.getItem('roles')
  const roles = rolesRaw ? JSON.parse(rolesRaw) : []
  const isAdmin = roles.includes('MASTER_ADMIN') || roles.includes('ADMIN')
  const init = isAdmin ? 'admin' : (localStorage.getItem('viewMode') || 'user')
  const [mode, setMode] = useState(init)
  const toggleMode = () => {
    const m = mode === 'admin' ? 'user' : 'admin'
    setMode(m)
    localStorage.setItem('viewMode', m)
    console.log(m);

  }

  // ====== Global Loading State ======
  const [globalLoading, setGlobalLoading] = useState({
    open: false,
    variant: 'spinner',   // 'spinner' | 'text' | 'imageSplit'
    text: 'กำลังโหลดข้อมูล...',
    subtext: '',
    fullscreen: true,
    dimBackground: true,
    size: 56,
    imageUrl: '',
    imageAlt: 'Loading',
    split: '40/60',       // '40/60' | '60/40'
  })

  // รองรับการซ้อนหลาย ๆ งานพร้อมกัน (push/pop)
  const loadingCount = useRef(0)

  const showLoading = useCallback((opts = {}) => {
    loadingCount.current += 1
    setGlobalLoading(prev => ({
      ...prev,
      open: true,
      ...opts,
    }))
  }, [])

  const hideLoading = useCallback(() => {
    loadingCount.current = Math.max(loadingCount.current - 1, 0)
    if (loadingCount.current === 0) {
      setGlobalLoading(prev => ({ ...prev, open: false }))
    }
  }, [])

  // ====== Context ======
  const ctx = useMemo(
    () => ({
      mode,
      toggleMode,
      roles,
      showLoading,
      hideLoading,
      setGlobalLoading, // เผื่ออยาก set แบบกำหนดเองทั้งก้อน
    }),
    [mode, roles, showLoading, hideLoading]
  )

  return (
    <LayoutContext.Provider value={ctx}>
      <div className="flex h-screen min-h-0 w-full overflow-hidden">
        <div className="hidden md:block shrink-0">
          <Sidebar />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          {/* <TrialBanner /> */}
          <div className="sticky top-0 z-30 bg-white/80 backdrop-blur">
            <Topbar />
          </div>
          <main className="relative flex-1 min-w-0 overflow-auto p-2 sm:p-4">
            <div className="w-full max-w-full min-w-0">
              <Outlet />
            </div>
            <div id="drawer-root" />
          </main>
        </div>
      </div>

      <GlobalLoadingOverlay state={globalLoading} />
    </LayoutContext.Provider>
  )
}
